import { useEffect, useState } from "react";
import Spinner from "../layout/Spinner";
import { useAppDispatch, useAppSelector } from "../../hooks";
import { validatePaymentPin } from "../../slices/confirmation";
import { translateText } from "../../utils/translation";
import SpaceFiller from "../layout/SpaceFiller";
import { addAlert } from "../../slices/alert";
import { generateAlertText } from "../../utils/boilerplate";
import Banner from "../layout/Banner";

export default function Payment2FA() {
  const dispatch = useAppDispatch();

  // Extract data from Redux store
  const {
    payment: {
      two_factor_authentication: { data, loading, redirecting, attempted },
    },
  } = useAppSelector((state) => state.confirmation);

  // When redirect URL has been received, redirect to payment portal
  useEffect(() => {
    if (data === null) return;
    window.location.replace(data);
    return;
  }, [data]);

  useEffect(() => {
    if (!attempted) return;
    if (loading) return;
    if (redirecting) {
      dispatch(
        addAlert(generateAlertText("redirecting", language), "success", 5000),
      );
      return;
    }
    dispatch(
      addAlert(generateAlertText("payment_pin_fail", language), "danger", 5000),
    );
    return;
  }, [dispatch, attempted, loading, redirecting]);

  // Declare component level state
  const [fullPinInput, setFullPinInput] = useState<{ [key: string]: string }>({ value: "" });

  useEffect(() => {
    if (fullPinInput.value.length === 4) {
      submitPin(fullPinInput.value.toUpperCase());
      return;
    }
  }, [fullPinInput]);

  function submitPin(pinString: string) {
    dispatch(validatePaymentPin({ pin: pinString }));
  }

  // Declare constants
  const language = "EN";
  const validChars = "abcdefghijklmnopqrstuvwxyz1234567890";

  return (
    <div className="flex h-screen mx-auto max-w-7xl py-24 sm:px-6 sm:py-32 lg:px-8 items-center">
      <form className="max-w-sm mx-auto">
        {loading || redirecting ? (
          <SpaceFiller>
            <Spinner />
          </SpaceFiller>
        ) : (
          <>

            {/* Pin input v2 */}
            <div className="flex mb-2 sm:mx-0 mx-2 space-x-2 rtl:space-x-reverse justify-center">
              <input
                onChange={(e) => {
                  const chars = e.target.value;
                  let check = false;
                  for (let i = 0; i < chars.length; i++) {
                    if (!validChars.includes(chars[i].toLowerCase())) {
                      check = true;
                      break;
                    }
                  }
                  if (!check) {
                    setFullPinInput({ ...fullPinInput, value: e.target.value })
                  }
                }
                }
                autoFocus
                type="text"
                maxLength={4}
                id="pin-full"
                className="block w-full h-12 py-3 text-sm font-extrabold text-center text-midnight bg-slate-500/40 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
                value={fullPinInput.value.toUpperCase()}
                required
              />
            </div>

            {/* Explanation text */}
            <p
              id="helper-text-explanation"
              className="text-center mt-4 text-sm text-gray-600"
            >
              {translateText("payment_pin_validation", language)}
            </p>
          </>
        )}
      </form>

      {/* Alert banner */}
      <Banner />
    </div>
  );
}
